import { forgotPasswordSchema } from '@hemmafixa/shared';
import { Button, FormField, Input } from '@hemmafixa/ui';
import { MailCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { useForgotPassword } from './api';
import { authErrorMessage } from './auth-errors';
import { AuthPage } from './auth-page';

export function ForgotPasswordPage() {
  const { t } = useI18n();
  const location = useLocation();
  const forgot = useForgotPassword();
  const [email, setEmail] = useState(() => (location.state as { email?: string } | null)?.email ?? '');
  const [error, setError] = useState<string>();

  const backToLogin = (
    <Link to="/login" className="font-semibold text-foreground underline-offset-4 hover:underline">
      {t.auth.backToLogin}
    </Link>
  );

  // The API answers the same whether the account exists or not, and so does this page
  if (forgot.isSuccess) {
    return (
      <AuthPage title={t.auth.sentTitle} footer={backToLogin}>
        <div className="flex gap-3 text-sm">
          <MailCheck className="size-5 shrink-0 text-success" aria-hidden />
          <p role="status">{fill(t.auth.sentText, { email: forgot.variables })}</p>
        </div>
      </AuthPage>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = forgotPasswordSchema.safeParse({ email });
    if (!parsed.success) return setError(t.auth.emailInvalid);
    setError(undefined);
    forgot.mutate(parsed.data.email);
  };

  return (
    <AuthPage title={t.auth.forgotTitle} description={t.auth.forgotDescription} footer={backToLogin}>
      <form className="grid gap-4" onSubmit={submit} noValidate>
        {forgot.isError && (
          <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm font-medium text-danger">
            {authErrorMessage(forgot.error, t)}
          </p>
        )}
        <FormField label={t.auth.email} error={error} required>
          <Input
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </FormField>
        <Button type="submit" size="lg" loading={forgot.isPending}>
          {t.auth.sendLink}
        </Button>
      </form>
    </AuthPage>
  );
}
